// The notification bell, shared by the desktop sidebar and the phone's top bar.
// It lived inside AdminLayout, which meant the field app — the people most likely to
// be sent something — had no bell at all.
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { linkForItem, linkLabelForItem } from '../utils/deepLink';
import BidFollowup from './BidFollowup';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken() || '', ...(opts.headers || {}) },
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw d;
    return d;
  });
}

// Only Executives (and the owner login) approve time off. Older request notices sent to
// PMs and the office before that rule still exist — they just lose their buttons.
const CAN_ANSWER_TIME_OFF = (() => { try { return ['admin', 'Executive'].includes(JSON.parse(localStorage.getItem('titan_session') || 'null')?.role); } catch { return false; } })();

export default function NotificationBell({ placement = 'sidebar' }) {
  const navigate = useNavigate();   // notifications open the item they're about
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({});
  const [reviewing, setReviewing] = useState(null);
  const [followBid, setFollowBid] = useState(null);   // a bid follow-up opened from its bell (Ryan, Oct 11 2026)
  // Two lists behind one bell: what came to me, and what I sent and whether it landed.
  const [tab, setTab] = useState('inbox');
  const [sent, setSent] = useState([]);
  const ref = useRef(null);
  const panelRef = useRef(null);   // the dropdown, drawn on the page itself so no tile can cover it (Oct 9 2026)

  // The bell lives at the bottom of the sidebar, so anchor the panel just above-and-right
  // of it with fixed positioning (escapes the sidebar's clipping) instead of dropping down.
  function toggleOpen() {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setCoords(placement === 'topbar'
        // Under the bell, but never past the edge of the screen — on a phone the bell
        // sits mid-bar, and hanging a 340px panel off it puts half of it out of view.
        ? { top: r.bottom + 8, right: Math.max(8, Math.min(window.innerWidth - r.right, window.innerWidth - 348)) }
        : { left: r.right + 8, bottom: Math.max(12, window.innerHeight - r.bottom) });
    }
    setOpen(o => !o);
  }

  function load() { apiFetch('/notifications').then(d => Array.isArray(d) ? setNotifs(d) : null).catch(() => {}); }
  // Only fetched when asked for — most opens of the bell are to read, not to check up.
  useEffect(() => {
    if (open && tab === 'sent') apiFetch('/notifications/sent').then(d => Array.isArray(d) ? setSent(d) : null).catch(() => {});
  }, [open, tab]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target) && !(panelRef.current && panelRef.current.contains(e.target))) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('touchstart', onClick); };
  }, []);

  const unread = notifs.filter(n => !n.read).length;

  async function markRead(id) {
    await apiFetch(`/notifications/${id}/read`, { method: 'PATCH' });
    setNotifs(ns => ns.map(n => n.id === id ? { ...n, read: 1 } : n));
  }

  async function markAllRead() {
    await apiFetch('/notifications/mark-all-read', { method: 'PATCH' });
    setNotifs(ns => ns.map(n => ({ ...n, read: 1 })));
  }

  async function review(requestId, status) {
    setReviewing(requestId);
    try { await apiFetch(`/time-off/${requestId}`, { method: 'PATCH', body: JSON.stringify({ status }) }); load(); }
    catch {}
    setReviewing(null);
  }

  const statusStyle = s => ({
    pending:  { background: '#fef3c7', color: '#92400e' },
    approved: { background: '#d1fae5', color: '#065f46' },
    denied:   { background: '#fee2e2', color: '#991b1b' },
  }[s] || {});

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={toggleOpen}
        style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2a6 6 0 00-6 6v3l-1.5 2.5h15L16 11V8a6 6 0 00-6-6z" stroke="currentColor" strokeWidth="1.4" fill="none"/><path d="M8 16.5a2 2 0 004 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, background: '#dc2626', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {/* Drawn at the top of the page (a portal): inside the side menu it sat in the menu's layer, and the
          bid / job tiles drew over it (Pat, Oct 9 2026). */}
      {open && createPortal(
        <div ref={panelRef} style={{ position: 'fixed', ...coords, width: 340, maxWidth: 'calc(100vw - 24px)', maxHeight: '75vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', border: '1px solid #e5e7eb', zIndex: 3000, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {[['inbox', 'Notifications'], ['sent', 'Sent']].map(([k, lbl]) => (
                <button key={k} onClick={() => setTab(k)}
                  style={{ background: tab === k ? '#f3f4f6' : 'none', border: 'none', borderRadius: 7,
                    padding: '4px 10px', cursor: 'pointer', fontSize: 13, fontWeight: tab === k ? 800 : 600,
                    color: tab === k ? '#111' : '#9ca3af' }}>{lbl}</button>
              ))}
            </div>
            {tab === 'inbox' && unread > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6366f1' }}>Mark all read</button>}
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {tab === 'sent' ? (
              sent.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  You haven’t sent anyone a page yet.
                </div>
              ) : sent.map(s => {
                const d = s.data ? (() => { try { return JSON.parse(s.data); } catch { return {}; } })() : {};
                return (
                  <div key={s.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f9fafb' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>
                      {d.label || 'a page'} <span style={{ color: '#6b7280', fontWeight: 400 }}>→ {s.to_name}</span>
                    </div>
                    {s.body && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{s.body}</div>}
                    {/* The whole point of keeping this apart from a directive: you learn
                        whether it was seen, and nothing more is asked of anybody. */}
                    <div style={{ fontSize: 11, marginTop: 5 }}>
                      <span style={{ color: '#9ca3af' }}>{new Date(s.created_at).toLocaleString()} · </span>
                      {s.read
                        ? <span style={{ color: '#15803d', fontWeight: 700 }}>opened</span>
                        : <span style={{ color: '#b45309', fontWeight: 700 }}>not opened yet</span>}
                    </div>
                  </div>
                );
              })
            ) : notifs.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No notifications</div>
            ) : notifs.map(n => {
              const data = n.data ? (() => { try { return JSON.parse(n.data); } catch { return {}; } })() : {};
              const isTimeOff = n.type === 'time_off_request';
              // Tapping a notification opens the thing it's about — the PO card, the
              // drilling card, the submittal — not just the job it belongs to.
              const target = isTimeOff ? null : linkForItem({ type: n.type, data });
              const go = () => {
                if (!n.read) markRead(n.id);
                if (target) { setOpen(false); navigate(target); }
              };
              return (
                <div key={n.id} onClick={go}
                  style={{ padding: '12px 16px', borderBottom: '1px solid #f9fafb', background: n.read ? '#fff' : '#f0f4ff', cursor: (target || !n.read) ? 'pointer' : 'default' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: isTimeOff ? '#fef3c7' : '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14 }}>
                      {isTimeOff ? '📅' : '✓'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 2 }}>{n.title}</div>
                      {n.body && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{n.body}</div>}
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                        {new Date(n.created_at).toLocaleString()}
                        {target && <span style={{ color: '#6366f1', fontWeight: 700 }}> · {linkLabelForItem({ type: n.type, data })} ›</span>}
                      </div>
                      {n.type === 'bid_followup' && data.bid_id && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button onClick={e => { e.stopPropagation(); if (!n.read) markRead(n.id); setOpen(false); setFollowBid(data.bid_id); }}
                            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            {data.kind === 'told_low' ? '✉ Email & check project status' : '✉ Email bid receipt & check status'}
                          </button>
                        </div>
                      )}
                      {isTimeOff && data.request_id && CAN_ANSWER_TIME_OFF && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button onClick={e => { e.stopPropagation(); review(data.request_id, 'approved'); }}
                            disabled={reviewing === data.request_id}
                            style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Approve</button>
                          <button onClick={e => { e.stopPropagation(); review(data.request_id, 'denied'); }}
                            disabled={reviewing === data.request_id}
                            style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Deny</button>
                        </div>
                      )}
                    </div>
                    {!n.read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', flexShrink: 0, marginTop: 4 }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>, document.body
      )}
      {followBid && <BidFollowup bidId={followBid} onClose={() => setFollowBid(null)} onDone={load} />}
    </div>
  );
}
